import { Everything } from '@hit/amorphic-types';
import { property, supertypeClass } from '@haventech/amorphic';
import { Application } from '../customer/Application';
import { Customer } from '../customer/Customer';
import { QuoteInterface } from '../interfaces/QuoteInterface';
import { QuoteRider } from './QuoteRider';
import { QuoteExchange1035 } from './coverpath/QuoteExchange1035';
import { RiderType } from '../static/Product';
import { CommonUtils } from '../static/CommonUtils';
import { Assumptions } from '../static/Assumptions';

@supertypeClass({ toClient: true, toServer: true })
export abstract class BaseQuote extends Everything implements QuoteInterface {
  init(customer: Customer): BaseQuote {
    this.customer = customer;
    return this;
  }

  @property({
    getType: () => Customer,
    comment: `an instance of Customer`,
  })
  customer: Customer;

  @property({
    comment: 'the type of product, such as term',
  })
  productType: 'Whole' | 'Term' | 'Annuity';

  @property({
    comment: `product code, e.g. which insurance product is this a quote for?`,
  })
  product: string = '';

  @property({
    comment: `amount of death benefit an individual can receive`,
    rule: ['currency'],
    validate: 'isWithin(100000, 999999999)',
  })
  faceAmount: number;

  @property({
    comment: `true if the individual consumes nicotine containing products`,
  })
  smoker: boolean = false;

  @property({
    comment: `date of birth of an individual`,
    sensitiveData: true,
  })
  dateOfBirth: Date;

  @property({
    comment: `gender of the individual`,
    values: ['1', '2'],
    descriptions: {
      '1': 'Male',
      '2': 'Female',
    },
  })
  gender: '1' | '2';

  @property({
    comment: `citizenship of the individual`,
    values: ['citizen', 'residentAlien', 'nonResidentAlien', 'none'],
    descriptions: {
      citizen: 'U.S. Citizen and reside in the U.S.',
      residentAlien: 'Non-U.S. Citizen and reside in the U.S. as a Permanent Resident (Green Card Holder)',
      nonResidentAlien: 'Non-Resident Alien',
      none: 'Other',
    },
  })
  citizenship: 'citizen' | 'residentAlien' | 'nonResidentAlien' | 'none';

  @property({
    comment: `base amount customer pays for insurance`,
  })
  premium: number;

  @property({
    comment: `base amount customer pays for insurance (without Extended Period of Convertibility)`,
  })
  premiumWithoutECP: number;

  @property({
    comment: `base amount customer pays for insurance (with Extended Period of Convertibility)`,
  })
  premiumWithECP: number;

  @property({
    comment: 'the initial health class of the individual',
  })
  healthClass: string;

  @property()
  term: number;

  @property({
    rule: 'required',
    values: CommonUtils.getSupportedStateCodes(Assumptions.coverpathUnsupportedStatesTerm),
    descriptions: CommonUtils.getSupportedStateValues(Assumptions.coverpathUnsupportedStatesTerm),
  })
  contractState: string;

  @property({
    rule: 'required',
  })
  waiverOfPremium: boolean;

  @property()
  waiverOfPremiumAmount: number;

  @property({
    comment: `Payment option selected by user`,
    values: ['Annual', 'Monthly'],
    descriptions: {
      Annual: 'Yearly',
      Monthly: 'Monthly',
    },
  })
  paymentFrequency: string;

  @property()
  createdTime: Date;

  @property({
    comment: `Quotes marked as prior are not active`,
  })
  isPrior: boolean;

  @property({
    comment: 'designates whether the agent or the client created the quote',
  })
  isAdvisorQuote: boolean = true;

  @property({ isLocal: true })
  _hasEcp: boolean = false;

  @property({
    toServer: false,
    comment: 'for cas, ID of invitation for customer to complete policy application',
  })
  invite: string;

  @property({ getType: () => BaseQuote })
  secondaryQuote: BaseQuote;

  @property({ getType: () => Application, comment: 'application for this quote' })
  application: Application;

  @property({ getType: () => QuoteRider, comment: 'All the riders attached to this quote' })
  riders: Array<QuoteRider> = [];

  @property({ getType: () => QuoteExchange1035, comment: '1035 Exchange attached to this quote' })
  quoteExchange1035: QuoteExchange1035;

  @property()
  bankSale: boolean = false;

  @property({ comment: 'Tentative effective date attached to this quote' })
  effectiveDate: Date;

  /**
   * Does this quote have enough information for a 1035 exchange to proceed?
   * @returns {boolean}
   */
  has1035Exchange(): boolean {
    return this.quoteExchange1035 && !!this.quoteExchange1035.cashValueTransfer;
  }

  // Checks whether or not current Quote object can be used to fetch a premium
  abstract verifyProperties(): boolean;

  public hasBeenInitialized(): boolean {
    return !!this.contractState && !!this.gender && !!this.healthClass && !!this.faceAmount && !!this.term;
  }

  static calculateHealthClassCode(healthClassNumber: number, isSmoker: boolean): string {
    if (isSmoker) {
      return ['SELECT_TOBACCO', 'SELECT_TOBACCO', 'STANDARD_TOBACCO', 'STANDARD_TOBACCO'][healthClassNumber];
    } else {
      return ['ULTRA', 'SELECT', 'STANDARD', 'STANDARD'][healthClassNumber];
    }
  }

  static isSmoker(healthClass: string): boolean {
    return healthClass === 'SELECT_TOBACCO' || healthClass === 'STANDARD_TOBACCO';
  }

  getRider(type: RiderType): QuoteRider {
    return this.riders.find((rider) => {
      return rider.type === type;
    });
  }

  addRider(type: RiderType): QuoteRider {
    let rider = this.getRider(type);
    if (!rider) {
      rider = QuoteRider.new(type, this);
      this.riders.push(rider);
    }
    return rider;
  }

  getTotalPremium(): number {
    return this.premium + (this.waiverOfPremium ? this.waiverOfPremiumAmount : 0);
  }

  isWholeLifeQuote(): boolean {
    return false;
  }
}
